import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react'
import { LogoMark } from './Logo'
import { PROMPT_DIMENSIONS } from '@/lib/promptDimensions'
import { hintToCard, loadSuggestionTaxonomy, type PromptAnalysis, type PromptHint, type SuggestionCard } from '@/lib/api'
import { sampleUrlFor, sampleLabelFor, toggleSample, stopSample, playingUrl, subscribePlayback } from '@/lib/sampleExamples'

// Re-render the calling component whenever audio play/stop state changes.
function usePlayingUrl(): string | null {
  return useSyncExternalStore(subscribePlayback, playingUrl, () => null)
}
import { describeValue, groupSuggestions, popularFor } from '@/lib/suggestionInfo'
import { subscribeTaxonomy, getTaxonomyVersion } from '@/lib/taxonomyStore'
import { INTENTS } from '@/lib/intents'

// Dimensions whose browse-all modal groups by family in collapsible accordions (the pool is
// large enough that flat would be unscannable). Others render their pool as one flat group.
const GROUPED_DIMS = new Set(['genre', 'mood'])

function tierBorder(score: number) {
  if (score >= 85) return 'border-emerald-700'
  if (score >= 60) return 'border-primary-700'
  if (score >= 35) return 'border-amber-700'
  return 'border-rose-700'
}
function tierText(score: number) {
  if (score >= 85) return 'text-emerald-300'
  if (score >= 60) return 'text-primary-300'
  if (score >= 35) return 'text-amber-300'
  return 'text-rose-300'
}

const SCORE_BLURB: Record<string, string> = {
  Excellent: 'Covers every key dimension — ready for Suno.',
  Strong: 'Covers most dimensions; a tweak or two to go.',
  'Getting there': 'A few key dimensions are still missing.',
  'Just started': 'Add a genre, instruments and more to strengthen it.',
}

const DIM_BY_KEY = Object.fromEntries(PROMPT_DIMENSIONS.map((d) => [d.key, d]))

// Which hint sits under a viewport point — found by hit-testing the rendered <mark> rects in the
// backdrop layer (which is pixel-aligned with the textarea), not the caret-from-point APIs. Those
// APIs return offsets relative to the wrong node inside a Shadow DOM (the extension mounts in one),
// so a click anywhere would resolve to the first hint. getClientRects() is shadow-safe and handles
// phrases that wrap across lines (one rect per line). Returns the hint id, or null off any mark.
function hintIdAt(container: HTMLElement | null, x: number, y: number): string | null {
  if (!container) return null
  for (const mark of container.querySelectorAll<HTMLElement>('[data-hint-id]')) {
    const id = mark.dataset.hintId
    if (!id) continue
    for (const r of mark.getClientRects()) {
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id
    }
  }
  return null
}

// The comma-separated descriptor surrounding a character position (so swapping a covered
// dimension replaces the whole phrase, not just the matched keyword).
function tokenAround(text: string, pos: number) {
  const before = text.lastIndexOf(',', pos - 1)
  const after = text.indexOf(',', pos)
  return text.slice(before === -1 ? 0 : before + 1, after === -1 ? text.length : after).trim()
}

// Mirror the prompt text and underline vague words and bare nouns in place. Positions come from the
// analyzer; guard against stale offsets mid-typing by confirming the slice still matches. The hint
// matching hoveredId is emphasized so it stays in sync with the hovered report row (and vice-versa).
function highlightSegments(text: string, hints: PromptHint[], hoveredId: string | null): ReactNode[] {
  const marks = hints
    .filter(
      (h) =>
        (h.kind === 'vague_word' || h.kind === 'bare_noun' || h.kind === 'present_dimension') &&
        h.start != null &&
        h.length != null,
    )
    .filter((h) => text.slice(h.start!, h.start! + h.length!).toLowerCase() === (h.term ?? '').toLowerCase())
    .sort((a, b) => a.start! - b.start!)

  const nodes: ReactNode[] = []
  let i = 0
  marks.forEach((h, idx) => {
    const s = h.start!
    const e = s + h.length!
    if (s < i) return
    if (s > i) nodes.push(<span key={`t${idx}`}>{text.slice(i, s)}</span>)
    const active = h.id != null && h.id === hoveredId
    // Covered dimensions get a subtle dotted green underline (click for more of the category);
    // flagged words get a light amber highlight (click to swap). Hover only brightens the
    // underline — the fill stays constant so the word never gets washed out.
    // Always set an explicit background — <mark>'s user-agent default is bright yellow, which
    // would otherwise wash out the word. Hover only brightens the underline; the fill stays put.
    const cls =
      h.kind === 'present_dimension'
        ? `rounded-[2px] bg-emerald-400/10 underline decoration-dotted decoration-2 underline-offset-2 ${
            active ? 'decoration-emerald-300' : 'decoration-emerald-500/50'
          }`
        : `rounded-[2px] bg-amber-400/15 underline decoration-2 underline-offset-2 ${
            active ? 'decoration-amber-200' : 'decoration-amber-400/80'
          }`
    nodes.push(
      <mark key={`m${idx}`} data-hint-id={h.id ?? ''} className={`text-transparent ${cls}`}>
        {text.slice(s, e)}
      </mark>,
    )
    i = e
  })
  nodes.push(<span key="tail">{`${text.slice(i)}\n`}</span>)
  return nodes
}

interface Props {
  value: string
  onChange: (v: string) => void
  analysis: PromptAnalysis
  onApply: (card: SuggestionCard, suggestion: string) => void
  maxLength: number
  /** Active intent preset + setter; when given, the report panel shows the "what are you
   *  making?" picker. Omitted by callers that don't re-score (the editor still renders). */
  intent?: string
  onIntentChange?: (intent: string) => void
  /** Turn the Promptioso evaluator off (hand the field back to a plain textarea / Grammarly). */
  onTurnOff?: () => void
}

// Always-writable Style editor with the score and analysis built into the field. The bottom
// stat bar shows score + good/warnings/missing counts; clicking the logo (or a count) opens
// a full report — score %, a one-line read, and detailed Good / Missing / Warnings sections —
// with a corner "Turn off" (à la Grammarly).
// Which slice of the report is open: the full report (logo), or a single section
// scoped to the stat-bar count that was clicked.
type ReportView = 'report' | 'good' | 'missing' | 'warnings' | null

// One way to apply a chosen value. A covered word offers two (Add more / Swap) so the modal
// can toggle between them; warnings and missing dimensions offer just one.
interface PickAction {
  key: string
  label: string
  prompt: string
  onPick: (s: string) => void
}

// Everything the "browse all" modal needs to render one dimension's full pool (grouped, with
// descriptions + audio) and apply a choice back to the prompt.
interface ExploreData {
  icon: string
  label: string
  dimension: string | null
  suggestions: string[]
  actions: PickAction[]
  initialActionKey?: string
}

// One entry in a report section: a plain button (icon + label) that opens the browse-all modal
// for its dimension. `hintId` ties good/warning buttons to their field underline for the
// hover sync; missing/refine entries have none.
interface DimEntry {
  selKey: string
  icon: string
  label: string
  tone: string
  hintId?: string
  actions: PickAction[]
  suggestions: string[]
  dimension: string | null
}

export default function SmartStyleEditor({ value, onChange, analysis, onApply, maxLength, intent, onIntentChange, onTurnOff }: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null)
  const backRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<ReportView>(null)
  // The report panel drops below the field by default, but flips above (and caps its height) when
  // the field sits near the bottom of the viewport — otherwise the lower rows would be unreachable
  // (the extension pins the editor over Suno's box, which can be low on screen / non-scrolling).
  const [panelPos, setPanelPos] = useState<{ up: boolean; maxHeight: number }>({ up: false, maxHeight: 320 })

  // Re-render when the server's suggestion taxonomy overlays the bundled default, and kick off
  // that one-time fetch — so catalog updates appear without an extension rebuild.
  useSyncExternalStore(subscribeTaxonomy, getTaxonomyVersion, getTaxonomyVersion)
  useEffect(() => {
    void loadSuggestionTaxonomy()
  }, [])
  // Hint id shared by the field underline and its report row — hovering either end lights both.
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  // The "browse all" modal, when open (opened by clicking a dimension button or a marked word).
  const [explore, setExplore] = useState<ExploreData | null>(null)
  // selKey of the report row whose compact inline picker is expanded (one at a time).
  const [openKey, setOpenKey] = useState<string | null>(null)

  // Stop any audio preview whenever the open surface changes — closing or switching
  // menus (report panel or browse-all modal) both cut the clip.
  const surface = explore ? 'explore' : view ? 'view' : null
  const prevSurface = useRef(surface)
  useEffect(() => {
    if (prevSurface.current !== surface) {
      stopSample()
      prevSurface.current = surface
    }
  }, [surface])

  // On open, measure the field's room below vs. above and decide which way the panel drops and how
  // tall it can be. Re-measures on scroll/resize while open so it stays correct as the page moves.
  useLayoutEffect(() => {
    if (!view) return
    const place = () => {
      const r = rootRef.current?.getBoundingClientRect()
      if (!r) return
      const margin = 12
      const below = window.innerHeight - r.bottom - margin
      const above = r.top - margin
      const up = below < 280 && above > below
      setPanelPos({ up, maxHeight: Math.max(180, Math.floor(up ? above : below)) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [view])

  function show(next: ReportView) {
    setOpenKey(null)
    setView(next)
  }
  function close() {
    setOpenKey(null)
    setView(null)
  }

  // Phrasing warnings: vague words + bare nouns are one-click replaceable (term + swaps);
  // prose is a single advisory nudge with no token to swap.
  const replaceable = analysis.hints.filter((h) => h.kind === 'vague_word' || h.kind === 'bare_noun')
  // Positionless advisories: prose nudges plus the coherence axis (contradictions / over-blend).
  // No token to swap — they render as a short "double-check this" line.
  const advisories = analysis.hints.filter(
    (h) => h.kind === 'prose' || h.kind === 'conflict' || h.kind === 'dilution',
  )
  const warningCount = replaceable.length + advisories.length
  const present = analysis.hints.filter((h) => h.kind === 'present_dimension')
  // Every word marked in the field (covered + flagged) that has a position to click on.
  const clickable = analysis.hints.filter(
    (h) =>
      (h.kind === 'vague_word' || h.kind === 'bare_noun' || h.kind === 'present_dimension') &&
      h.start != null &&
      h.length != null,
  )
  const missing = new Map<string, PromptHint>()
  analysis.hints
    .filter((h) => h.kind === 'missing_dimension' && h.dimension)
    .forEach((h) => missing.set(h.dimension as string, h))
  // The active intent decides what's graded: required-but-missing dimensions feed the
  // "missing" count; everything else missing (polish tier + dimensions the intent doesn't
  // grade, e.g. vocals for an instrumental) is shown as optional refinement, never counted.
  const required = new Set(analysis.requiredDimensions)
  const missingDims = PROMPT_DIMENSIONS.filter((d) => required.has(d.key) && missing.has(d.key))
  const optionalDims = PROMPT_DIMENSIONS.filter((d) => !required.has(d.key) && missing.has(d.key))

  function apply(hint: PromptHint, suggestion: string) {
    onApply(hintToCard(hint), suggestion)
  }

  // Swap a covered dimension's value: replace the whole descriptor token, not just the
  // matched keyword, so "fingerpicked acoustic guitar" → "warm analog synths" cleanly.
  function swap(hint: PromptHint, suggestion: string) {
    const token = hint.start != null ? tokenAround(value, hint.start) : ''
    const original = token && token.toLowerCase().includes((hint.term ?? '').toLowerCase()) ? token : hint.term
    onApply({ id: hint.id, kind: hint.kind, dimension: hint.dimension, original, suggestions: hint.suggestions, reason: hint.message }, suggestion)
  }

  // Add another descriptor of the same dimension (no `original`, so it appends instead of
  // replacing) — the "add more" action offered when clicking a covered (good) word.
  function addMore(hint: PromptHint, suggestion: string) {
    onApply({ id: hint.id, kind: hint.kind, dimension: hint.dimension, original: null, suggestions: hint.suggestions, reason: hint.message }, suggestion)
  }

  // A covered word offers two actions — Add more (primary) and Swap — so the modal can toggle.
  function goodActions(hint: PromptHint): PickAction[] {
    const label = String(DIM_BY_KEY[hint.dimension ?? '']?.label ?? 'option')
    return [
      { key: 'add', label: 'Add more', prompt: `Add another ${label} to your prompt`, onPick: (s) => addMore(hint, s) },
      { key: 'swap', label: 'Swap', prompt: `Swap this ${label} for another`, onPick: (s) => swap(hint, s) },
    ]
  }
  // Single-action surfaces (missing dimensions, phrasing warnings) just apply the choice.
  function applyAction(hint: PromptHint, key: string, label: string, prompt: string): PickAction[] {
    return [{ key, label, prompt, onPick: (s) => apply(hint, s) }]
  }

  // Build the report entries for one section — each is a plain button that opens the browse-all
  // modal for its dimension (with the right add/swap actions for its kind).
  const goodEntries: DimEntry[] = present.map((h) => {
    const d = DIM_BY_KEY[h.dimension ?? ''] ?? { icon: '🎵', label: h.dimension }
    return { selKey: `good:${h.id}`, icon: d.icon, label: String(d.label), tone: 'text-emerald-300', hintId: h.id, actions: goodActions(h), suggestions: h.suggestions, dimension: h.dimension }
  })
  const missingEntries: DimEntry[] = missingDims.map((d) => {
    const hint = missing.get(d.key) as PromptHint
    return { selKey: `missing:${d.key}`, icon: d.icon, label: d.label, tone: 'text-gray-200', actions: applyAction(hint, 'add', 'Add', hint.message), suggestions: hint.suggestions, dimension: d.key }
  })
  const optionalEntries: DimEntry[] = optionalDims.map((d) => {
    const hint = missing.get(d.key) as PromptHint
    return { selKey: `optional:${d.key}`, icon: d.icon, label: d.label, tone: 'text-gray-300', actions: applyAction(hint, 'add', 'Add', hint.message), suggestions: hint.suggestions, dimension: d.key }
  })
  const warningEntries: DimEntry[] = replaceable.map((h, i) => ({
    selKey: `warn:${i}`, icon: '⚠', label: h.term ?? 'this word', tone: 'text-amber-300', hintId: h.id, actions: applyAction(h, 'swap', 'Swap', h.message), suggestions: h.suggestions, dimension: h.dimension,
  }))

  // Open the browse-all modal for a dimension button, preserving the active add/swap mode.
  const exploreEntry = (e: DimEntry, activeKey: string) => {
    setOpenKey(null)
    setExplore({ icon: e.icon, label: e.label, dimension: e.dimension, suggestions: e.suggestions, actions: e.actions, initialActionKey: activeKey })
  }

  // Clicking a marked word opens the same browse-all modal for just that word — more options of
  // its category for a covered dimension, swaps for a phrasing warning.
  function openWord(hint: PromptHint) {
    const good = hint.kind === 'present_dimension'
    const d = DIM_BY_KEY[hint.dimension ?? ''] ?? { icon: '🎵', label: hint.dimension }
    const actions = good ? goodActions(hint) : applyAction(hint, 'swap', 'Swap', hint.message)
    setExplore({
      icon: good ? d.icon : '⚠',
      label: good ? String(d.label) : (hint.term ?? 'this word'),
      dimension: hint.dimension,
      suggestions: hint.suggestions,
      actions,
      initialActionKey: actions[0].key,
    })
  }

  return (
    <div ref={rootRef} className={`relative rounded-xl border bg-gray-900 p-2 focus-within:ring-1 focus-within:ring-primary-700/50 ${tierBorder(analysis.score)}`}>
      <div className="relative">
        <div
          ref={backRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-1 py-1 font-mono text-sm text-transparent"
        >
          {highlightSegments(value, analysis.hints, hoveredId)}
        </div>
        <textarea
          ref={taRef}
          value={value}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
          onScroll={() => {
            if (backRef.current && taRef.current) backRef.current.scrollTop = taRef.current.scrollTop
          }}
          // Light up the marked word directly under the cursor (null when over plain text).
          onMouseMove={(e: ReactMouseEvent<HTMLTextAreaElement>) => {
            const next = hintIdAt(backRef.current, e.clientX, e.clientY)
            setHoveredId((prev) => (prev === next ? prev : next))
          }}
          onMouseLeave={() => setHoveredId(null)}
          // A click only opens a menu when it lands on a marked word — swaps for a warning, more
          // options of the same category for a covered dimension. Clicking bare text does nothing.
          onClick={(e: ReactMouseEvent<HTMLTextAreaElement>) => {
            const id = hintIdAt(backRef.current, e.clientX, e.clientY)
            const hit = id ? clickable.find((h) => h.id === id) : null
            if (hit) openWord(hit)
          }}
          rows={3}
          placeholder="describe your track…"
          // Our highlight layer owns this field — keep Grammarly from injecting its own overlay.
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          className="relative w-full resize-none border-0 bg-transparent px-1 py-1 font-mono text-sm text-gray-100 caret-gray-100 outline-none placeholder:text-gray-600 focus:ring-0"
        />
      </div>

      {/* compact stat bar — the Promptioso score lives inside the field; opens the full report */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-800 pt-1.5 text-xs">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); show(view ? null : 'report') }}
          title="Open the prompt report"
          className={`flex items-center gap-1.5 font-semibold ${tierText(analysis.score)} hover:opacity-80`}
        >
          <LogoMark size="sm" className="h-4 w-4 shrink-0" />
          {analysis.score}%
          <span className="text-gray-500">{view ? '▴' : '▾'}</span>
        </button>

        <Count active={view === 'good'} onClick={() => show(view === 'good' ? null : 'good')} className="text-emerald-400/90">✓ {analysis.covered} good</Count>
        <Count active={view === 'warnings'} onClick={() => show(view === 'warnings' ? null : 'warnings')} className="text-amber-300">⚠ {warningCount} warning{warningCount === 1 ? '' : 's'}</Count>
        <Count active={view === 'missing'} onClick={() => show(view === 'missing' ? null : 'missing')} className="text-gray-400">
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
            <circle cx="8" cy="8" r="6.25" />
            <path d="M8 5.25v5.5M5.25 8h5.5" />
          </svg>
          {missingDims.length} missing
        </Count>
      </div>

      {view && <div className="fixed inset-0 z-20" onClick={close} />}

      {view && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: panelPos.maxHeight }}
          className={`absolute left-2 right-2 z-30 overflow-y-auto rounded-xl border border-gray-700 bg-gray-950 p-3 shadow-2xl ${
            panelPos.up ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {view === 'report' && (
            <div>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className={`flex items-center gap-2 text-sm font-bold ${tierText(analysis.score)}`}>
                    {analysis.score}% · {analysis.label}
                    {analysis.ready ? (
                      <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300">
                        ✓ Generate-ready
                      </span>
                    ) : (
                      analysis.coherence < 100 && (
                        <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
                          Coherence {analysis.coherence}%
                        </span>
                      )
                    )}
                  </div>
                  <p className="text-[11px] leading-snug text-gray-500">{SCORE_BLURB[analysis.label] ?? ''}</p>
                </div>
                {onTurnOff && (
                  <button
                    type="button"
                    onClick={() => { close(); onTurnOff() }}
                    title="Turn the Promptioso evaluator off on this field"
                    className="shrink-0 rounded-md border border-gray-700 px-2 py-1 text-[11px] text-gray-400 hover:bg-gray-800"
                  >
                    Turn off
                  </button>
                )}
              </div>
              {analysis.readback && (
                <p className="mt-2 rounded-md bg-gray-900/60 px-2 py-1.5 text-[11px] leading-snug text-gray-400">
                  🎧 <span className="text-gray-300">{analysis.readback}</span>
                </p>
              )}
              {intent && onIntentChange && (
                <IntentPicker intent={intent} onChange={onIntentChange} />
              )}
            </div>
          )}

          <div className={view === 'report' ? 'mt-3 space-y-3' : 'space-y-3'}>
            {(view === 'report' || view === 'good') && (
            <Section title="Good" tone="text-emerald-300" count={present.length} empty="Nothing detected yet.">
              <DimDrill
                entries={goodEntries}
                selected={openKey}
                onSelect={setOpenKey}
                onBack={() => setOpenKey(null)}
                onExplore={exploreEntry}
                hoveredId={hoveredId}
                onHover={setHoveredId}
              />
            </Section>
            )}

            {(view === 'report' || view === 'missing') && (
            <Section title="Missing" tone="text-gray-300" count={missingDims.length} empty="All dimensions covered 🎉">
              <DimDrill
                entries={missingEntries}
                selected={openKey}
                onSelect={setOpenKey}
                onBack={() => setOpenKey(null)}
                onExplore={exploreEntry}
                hoveredId={hoveredId}
                onHover={setHoveredId}
              />
            </Section>
            )}

            {(view === 'report' || view === 'missing') && optionalDims.length > 0 && (
            <Section title="Refine (optional)" tone="text-gray-400" count={optionalDims.length} empty="">
              <DimDrill
                entries={optionalEntries}
                selected={openKey}
                onSelect={setOpenKey}
                onBack={() => setOpenKey(null)}
                onExplore={exploreEntry}
                hoveredId={hoveredId}
                onHover={setHoveredId}
              />
            </Section>
            )}

            {(view === 'report' || view === 'warnings') && (
            <Section title="Warnings" tone="text-amber-300" count={warningCount} empty="No issues — clear and coherent 🎉">
              {advisories.map((h, i) => (
                <p
                  key={`adv:${i}`}
                  className={`rounded-md px-1 py-0.5 text-xs ${h.kind === 'conflict' ? 'text-rose-300' : 'text-amber-300'}`}
                >
                  {h.kind === 'conflict' ? '⚡' : '⚠'} <span className="text-gray-300">{h.message}</span>
                </p>
              ))}
              <DimDrill
                entries={warningEntries}
                selected={openKey}
                onSelect={setOpenKey}
                onBack={() => setOpenKey(null)}
                onExplore={exploreEntry}
                hoveredId={hoveredId}
                onHover={setHoveredId}
              />
            </Section>
            )}
          </div>
        </div>
      )}

      {/* Browse-all modal — opened by clicking a dimension button or a marked word. The "← Back"
          button only shows when the report menu is open underneath to return to. */}
      {explore && (
        <ExploreModal
          data={explore}
          onClose={() => setExplore(null)}
          onBack={view ? () => setExplore(null) : undefined}
        />
      )}
    </div>
  )
}

// "What are you making?" — the intent preset chips. Switching re-scores against a different
// required-dimension set (e.g. an instrumental track stops being docked for missing vocals).
function IntentPicker({ intent, onChange }: { intent: string; onChange: (key: string) => void }) {
  const active = INTENTS.find((i) => i.key === intent) ?? INTENTS[0]
  return (
    <div className="mt-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Making</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {INTENTS.map((i) => (
          <button
            key={i.key}
            type="button"
            onClick={() => onChange(i.key)}
            title={i.hint}
            className={`rounded-md border px-2 py-0.5 text-[11px] ${
              i.key === intent
                ? 'border-primary-500 bg-primary-950 text-primary-200'
                : 'border-gray-700 text-gray-400 hover:border-primary-700/70'
            }`}
          >
            {i.icon} {i.label}
          </button>
        ))}
      </div>
      <p className="mt-1 text-[11px] leading-snug text-gray-600">{active.hint}</p>
    </div>
  )
}

function Count({
  onClick,
  className,
  active,
  children,
}: {
  onClick: () => void
  className: string
  active?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick() }}
      className={`flex items-center gap-1 hover:underline ${active ? 'underline' : ''} ${className}`}
    >
      {children}
    </button>
  )
}

function Section({
  title,
  tone,
  count,
  empty,
  children,
}: {
  title: string
  tone: string
  count: number
  empty: string
  children?: ReactNode
}) {
  return (
    <div>
      <div className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ${tone}`}>
        {title} <span className="text-gray-500">({count})</span>
      </div>
      {count === 0 ? (
        <p className="mt-0.5 text-[11px] text-gray-600">{empty}</p>
      ) : (
        <div className="mt-1 space-y-1.5">{children}</div>
      )}
    </div>
  )
}

// A report section's body: a wrap of plain dimension buttons. Clicking one expands a compact
// inline picker for that dimension — a few smart, context-aware suggestions (genre-conditioned
// for instruments/percussion/etc., a popular set for genre) as one-tap chips, plus a search box
// and "View all →" that opens the full grouped browse-all modal. Good/warning buttons
// sync-highlight with their field underline on hover.
function DimDrill({
  entries,
  selected,
  onSelect,
  onBack,
  onExplore,
  hoveredId,
  onHover,
}: {
  entries: DimEntry[]
  selected: string | null
  onSelect: (k: string) => void
  onBack: () => void
  onExplore: (e: DimEntry, activeKey: string) => void
  hoveredId: string | null
  onHover: (id: string | null) => void
}) {
  const sel = entries.find((e) => e.selKey === selected)
  if (sel) {
    return (
      <div>
        <button type="button" onClick={onBack} className="mb-1 text-[11px] text-gray-500 hover:text-gray-300">
          ← back
        </button>
        <div className={`px-0.5 text-xs font-semibold ${sel.tone}`}>
          {sel.icon} {sel.label}
        </div>
        <Picker actions={sel.actions} suggestions={sel.suggestions} dimension={sel.dimension} onExplore={(ak) => onExplore(sel, ak)} afterPick={onBack} />
      </div>
    )
  }
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map((e) => {
        const active = e.hintId != null && e.hintId === hoveredId
        return (
          <button
            key={e.selKey}
            type="button"
            onClick={() => onSelect(e.selKey)}
            onMouseEnter={() => e.hintId && onHover(e.hintId)}
            onMouseLeave={() => onHover(null)}
            className={`rounded-md border px-2 py-0.5 text-[11px] ${e.tone} ${
              active ? 'border-primary-500 bg-gray-900' : 'border-gray-700 hover:border-primary-700/70'
            }`}
          >
            {e.icon} {e.label}
          </button>
        )
      })}
    </div>
  )
}

// Compact inline picker: the dimension's most relevant suggestions as one-tap chips (genre
// shows a curated popular set; every other dimension leads with its genre-conditioned picks),
// a search box that narrows the full pool, and "View all →" into the grouped browse-all modal.
// Keeps the long pools out of the way until the user wants them.
function Picker({
  actions,
  suggestions,
  dimension,
  onExplore,
  afterPick,
}: {
  actions: PickAction[]
  suggestions: string[]
  dimension: string | null
  onExplore: (activeKey: string) => void
  afterPick: () => void
}) {
  const [q, setQ] = useState('')
  const [modeIdx, setModeIdx] = useState(0)
  const active = actions[Math.min(modeIdx, actions.length - 1)]
  const needle = q.trim().toLowerCase()
  // At rest, lead with the smart/popular set; genre and mood are huge so they show a curated
  // popular set, others already arrive genre-conditioned-first from the backend pool. Search
  // always narrows the full pool.
  const popular = popularFor(dimension)
  const rest = popular
    ? popular.filter((p) => suggestions.some((s) => s.toLowerCase() === p.toLowerCase()))
    : suggestions
  const filtered = needle ? suggestions.filter((s) => s.toLowerCase().includes(needle)) : rest
  const candidates = filtered.slice(0, 14)

  return (
    <div className="mt-1 rounded-md bg-gray-900/60 p-1.5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="min-w-0 px-0.5 text-[11px] leading-snug text-gray-500">{active.prompt}</p>
        {actions.length > 1 && <ModeToggle actions={actions} active={modeIdx} onSelect={setModeIdx} />}
      </div>
      <div className="flex items-center gap-1">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${suggestions.length} options…`}
          className="min-w-0 flex-1 rounded-md border border-gray-700 bg-gray-950 px-2 py-1 text-xs text-gray-100 placeholder:text-gray-600 focus:border-primary-700 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => onExplore(active.key)}
          className="shrink-0 rounded-md border border-primary-700/70 px-2 py-1 text-[11px] text-primary-200 hover:bg-primary-950"
        >
          View all →
        </button>
      </div>
      {needle && filtered.length === 0 ? (
        <p className="mt-1.5 px-1 text-[11px] text-gray-600">No matches — try View all</p>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {candidates.map((s) => (
            <PickerChip key={s} value={s} dimension={dimension} onPick={() => { active.onPick(s); afterPick() }} />
          ))}
        </div>
      )}
    </div>
  )
}

// A compact picker chip: an optional ▶ preview (where a sample exists) joined to the value as
// an add button. The ▶ toggles audio without applying; the label applies and collapses the picker.
function PickerChip({ value, dimension, onPick }: { value: string; dimension: string | null; onPick: () => void }) {
  const url = sampleUrlFor(value, dimension)
  const playing = usePlayingUrl() === url
  return (
    <span className="inline-flex items-center overflow-hidden rounded-md border border-gray-700 bg-gray-950 hover:border-primary-700/70">
      {url && (
        <button
          type="button"
          aria-label={`${playing ? 'Stop' : 'Hear'} ${sampleLabelFor(value, dimension)} example`}
          onClick={() => toggleSample(url)}
          className="border-r border-gray-700 px-1.5 py-0.5 text-[11px] text-primary-300 hover:bg-primary-950"
        >
          {playing ? '■' : '▶'}
        </button>
      )}
      <button
        type="button"
        onClick={onPick}
        title={describeValue(value) ?? undefined}
        className="px-2 py-0.5 text-[11px] text-primary-200 hover:bg-primary-950"
      >
        + {value}
      </button>
    </span>
  )
}

// A segmented switch between the available actions — the active one is highlighted, the
// other(s) muted. Used for the covered-word "Add more" / "Swap" toggle.
function ModeToggle({
  actions,
  active,
  onSelect,
}: {
  actions: PickAction[]
  active: number
  onSelect: (i: number) => void
}) {
  return (
    <div className="flex shrink-0 gap-1">
      {actions.map((a, i) => (
        <button
          key={a.key}
          type="button"
          onClick={() => onSelect(i)}
          className={`rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            i === active
              ? 'border-primary-700/70 bg-primary-950 text-primary-200'
              : 'border-gray-700 text-gray-500 hover:text-gray-300'
          }`}
        >
          {a.label}
        </button>
      ))}
    </div>
  )
}

// The "browse all" modal: the full pool, grouped (genres by family, every other dimension as
// one group), each option carrying a short description of what it means + an audio preview
// where one exists. A search box narrows across every group.
function ExploreModal({ data, onClose, onBack }: { data: ExploreData; onClose: () => void; onBack?: () => void }) {
  const [q, setQ] = useState('')
  // Open in the action the caller asked for (add/swap); default to the first action.
  const [modeIdx, setModeIdx] = useState(() => {
    const i = data.actions.findIndex((a) => a.key === data.initialActionKey)
    return i >= 0 ? i : 0
  })
  // Which family accordions are expanded (grouped dims only). Families start collapsed so the
  // whole taxonomy is scannable, expand to reveal their values.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const active = data.actions[Math.min(modeIdx, data.actions.length - 1)]
  const needle = q.trim().toLowerCase()
  // Genre (500+) and mood (~90) are large enough that the browse-all shows every family as a
  // collapsible accordion, so the whole catalog is browsable by expanding — not only via search.
  // Every other dimension shows its whole pool as one flat group, as before.
  const isGrouped = data.dimension != null && GROUPED_DIMS.has(data.dimension)
  const noun = data.dimension === 'genre' ? { group: 'family', item: 'genres' } : { group: 'group', item: 'moods' }
  const values = needle ? data.suggestions.filter((s) => s.toLowerCase().includes(needle)) : data.suggestions
  const groups = groupSuggestions(data.dimension, values, data.label, active.prompt)
  const empty = groups.every((g) => g.items.length === 0)

  // Family accordion controls (grouped dims, at rest). A live search force-opens every group so
  // all matches are visible; otherwise families collapse so the full taxonomy stays scannable.
  const collapsible = isGrouped && !needle
  const familyKeys = groups.map((g) => g.key)
  const allOpen = familyKeys.length > 0 && familyKeys.every((k) => expanded.has(k))
  const isOpen = (key: string) => !collapsible || expanded.has(key)
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const n = new Set(prev)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 sm:p-8" onClick={onClose}>
      <div
        className="mt-6 flex max-h-[80vh] w-full max-w-2xl flex-col rounded-2xl border border-gray-700 bg-gray-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-800 p-4">
          <div className="flex min-w-0 items-start gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="mt-0.5 shrink-0 rounded-md border border-gray-700 px-1.5 py-0.5 text-[11px] text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              >
                ← Back
              </button>
            )}
            <div className="min-w-0">
              <div className="truncate text-sm font-bold text-gray-100">{data.icon} {data.label} — browse all</div>
              <p className="text-[11px] leading-snug text-gray-500">{active.prompt}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {data.actions.length > 1 && <ModeToggle actions={data.actions} active={modeIdx} onSelect={setModeIdx} />}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="border-b border-gray-800 p-3">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${data.suggestions.length} options…`}
            className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm text-gray-100 placeholder:text-gray-600 focus:border-primary-700 focus:outline-none"
          />
          {isGrouped && !needle && (
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <p className="text-[11px] text-gray-600">
                Expand a {noun.group} or search to browse all {data.suggestions.length} {noun.item}.
              </p>
              <button
                type="button"
                onClick={() => setExpanded(allOpen ? new Set() : new Set(familyKeys))}
                className="shrink-0 text-[11px] text-primary-300 hover:underline"
              >
                {allOpen ? 'Collapse all' : 'Expand all'}
              </button>
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {empty && <p className="text-xs text-gray-600">No matches.</p>}
          {groups.map((g) => {
            if (g.items.length === 0) return null
            const open = isOpen(g.key)
            const canCollapse = collapsible
            return (
              <div key={g.key}>
                {canCollapse ? (
                  <button
                    type="button"
                    onClick={() => toggle(g.key)}
                    className="flex w-full items-center gap-1 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-200"
                  >
                    <span className="w-3 text-gray-600">{open ? '▾' : '▸'}</span>
                    {g.label} <span className="text-gray-600">({g.items.length})</span>
                  </button>
                ) : (
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    {g.label} <span className="text-gray-600">({g.items.length})</span>
                  </div>
                )}
                {g.description && !isGrouped && (
                  <p className="mt-0.5 text-[11px] text-gray-600">{g.description}</p>
                )}
                {open && (
                  <div className="mt-1.5 grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
                    {g.items.map((v) => (
                      <ExploreItem key={v} value={v} dimension={data.dimension} onPick={(s) => { active.onPick(s); onClose() }} />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// One row in the browse-all modal: ▶ preview (where available) + the value as an add button,
// with its description (or the audio-sample label as a fallback hint) underneath.
function ExploreItem({ value, dimension, onPick }: { value: string; dimension: string | null; onPick: (s: string) => void }) {
  const url = sampleUrlFor(value, dimension)
  const playing = usePlayingUrl() === url
  const desc = describeValue(value) ?? sampleLabelFor(value, dimension)
  return (
    <div className="flex items-start gap-1.5">
      {url && (
        <button
          type="button"
          aria-label={`${playing ? 'Stop' : 'Hear'} ${sampleLabelFor(value, dimension)} example`}
          onClick={() => toggleSample(url)}
          className="mt-0.5 shrink-0 rounded-md border border-primary-700/70 px-1.5 py-0.5 text-xs text-primary-300 hover:bg-primary-950"
        >
          {playing ? '■' : '▶'}
        </button>
      )}
      <button type="button" onClick={() => onPick(value)} className="min-w-0 text-left">
        <div className="text-xs text-primary-200 hover:underline">+ {value}</div>
        {desc && <div className="text-[11px] leading-snug text-gray-500">{desc}</div>}
      </button>
    </div>
  )
}
